import React from 'react';
import { AlertCircle, Activity, CheckSquare, Clock, XCircle } from 'lucide-react';

const STAGE_LABELS = {
    all: 'All',
    not_started: 'Not Started',
    in_progress: 'In Progress',
    completed: 'Completed',
    overdue: 'Overdue',
};

const WorkstreamKpiStrip = ({
    selectorLabel = 'Workstream',
    workstreams = [],
    selectedWorkstreamKey,
    onSelectWorkstream,
    showDueMonthFilter = true,
    dueMonthLabel = 'Due Month',
    stageLabels = {},
    progressTitle = 'Progress',
    staffLoadTitle = 'Staff Load',
    dueMonthOptions = [],
    selectedDueMonth = 'all',
    onSelectDueMonth,
    stats,
    activeFilter = 'all',
    activeStaffFilter = 'all',
    onToggleFilter,
    onToggleStaffFilter,
    onClearFilter,
}) => {
    if (!stats) return null;

    const resolvedStageLabels = {
        not_started: stageLabels.not_started || 'Not Started',
        in_progress: stageLabels.in_progress || 'In Progress',
        completed: stageLabels.completed || 'Completed',
        overdue: stageLabels.overdue || 'Overdue',
    };

    const cards = [
        { id: 'not_started', icon: AlertCircle, iconCls: 'text-slate-400', label: resolvedStageLabels.not_started, count: stats.notStarted, pct: stats.pctNotStarted, valueCls: 'text-slate-700', pctCls: 'bg-slate-100 text-slate-500' },
        { id: 'in_progress', icon: Activity, iconCls: 'text-blue-500', label: resolvedStageLabels.in_progress, count: stats.inProgress, pct: stats.pctInProgress, valueCls: 'text-blue-700', pctCls: 'bg-blue-50 text-blue-600' },
        { id: 'completed', icon: CheckSquare, iconCls: 'text-emerald-500', label: resolvedStageLabels.completed, count: stats.completed, pct: stats.pctDone, valueCls: 'text-emerald-700', pctCls: 'bg-emerald-50 text-emerald-600' },
        { id: 'overdue', icon: Clock, iconCls: 'text-red-500', label: resolvedStageLabels.overdue, count: stats.overdue, pct: stats.pctOverdue, valueCls: 'text-red-600', pctCls: 'bg-red-50 text-red-600' },
    ];

    return (
        <div className="bg-slate-50 border-b border-slate-200 px-2 py-1.5 flex items-stretch gap-0 flex-none">
            <div className="bg-white px-2 py-1 rounded border border-slate-200 shadow-sm shrink-0 flex items-center gap-1.5 mr-2">
                <span className="text-[9px] font-semibold text-slate-500 uppercase">{selectorLabel}</span>
                <select
                    value={selectedWorkstreamKey}
                    onChange={(e) => onSelectWorkstream?.(e.target.value)}
                    className="text-[11px] border border-slate-200 rounded px-1.5 py-0.5 bg-white text-slate-700"
                >
                    {workstreams.map(workstream => (
                        <option key={workstream.key} value={workstream.key}>{workstream.shortLabel || workstream.label}</option>
                    ))}
                </select>
            </div>

            {showDueMonthFilter && onSelectDueMonth && (
                <div className="bg-white px-2 py-1 rounded border border-slate-200 shadow-sm shrink-0 flex items-center gap-1.5 mr-2">
                    <span className="text-[9px] font-semibold text-slate-500 uppercase">{dueMonthLabel}</span>
                    <select
                        value={selectedDueMonth}
                        onChange={(e) => onSelectDueMonth?.(e.target.value)}
                        className="text-[11px] border border-slate-200 rounded px-1.5 py-0.5 bg-white text-slate-700"
                    >
                        <option value="all">All</option>
                        {dueMonthOptions.map(month => (
                            <option key={month} value={month}>{month}</option>
                        ))}
                    </select>
                </div>
            )}

            <button
                type="button"
                onClick={() => onClearFilter?.()}
                disabled={activeFilter === 'all'}
                className={`px-2 py-1 rounded border text-[10px] font-semibold uppercase shrink-0 transition-all ${activeFilter === 'all' ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                title={activeFilter === 'all' ? 'No KPI filter active' : 'Clear KPI drill-down filter'}
            >
                Clear KPI Filter
            </button>

            {activeFilter !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-50 border border-blue-200 text-[10px] font-semibold text-blue-700 uppercase shrink-0 ml-1">
                    <XCircle className="w-3 h-3" /> Active: {STAGE_LABELS[activeFilter] || activeFilter}
                </span>
            )}

            {activeStaffFilter !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-indigo-50 border border-indigo-200 text-[10px] font-semibold text-indigo-700 uppercase shrink-0 ml-1">
                    <XCircle className="w-3 h-3" /> Staff Filter Active
                </span>
            )}

            <div className="w-px bg-slate-200 mx-1 self-stretch shrink-0" />

            {cards.map(({ id, icon: Icon, iconCls, label, count, pct, valueCls, pctCls }) => (
                <button
                    key={label}
                    type="button"
                    onClick={() => onToggleFilter?.(id)}
                    className={`flex-1 min-w-0 bg-white mx-1 px-2 py-1 rounded border shadow-sm flex items-center justify-between gap-1.5 text-left transition-all ${activeFilter === id ? 'border-blue-500 ring-1 ring-blue-400 bg-blue-50/30' : 'border-slate-200 hover:bg-slate-50'}`}
                    title={activeFilter === id ? `Clear ${label} filter` : `Filter by ${label}`}
                >
                    <div className="flex items-center gap-1 min-w-0">
                        <Icon className={`w-3 h-3 shrink-0 ${iconCls}`} />
                        <span className="text-[9px] font-semibold text-slate-500 uppercase truncate">{label}</span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                        <span className={`text-sm font-bold ${valueCls}`}>{count}</span>
                        <span className={`text-[9px] font-bold px-1 py-0.5 rounded ${pctCls}`}>{pct}%</span>
                    </div>
                </button>
            ))}

            <div className="w-px bg-slate-200 mx-1 self-stretch shrink-0" />

            <div className="flex-[2] min-w-0 flex flex-col justify-center gap-0.5 mx-1 px-2 py-1 bg-white rounded border border-slate-200 shadow-sm">
                <div className="flex items-center justify-between">
                    <span className="text-[9px] font-semibold text-slate-400 uppercase">{progressTitle} — {stats.total} clients</span>
                    <span className="text-[9px] font-bold text-emerald-600">{stats.pctDone}% done</span>
                </div>
                <div className="flex h-2 rounded-full overflow-hidden bg-slate-200 w-full">
                    <div className="bg-emerald-500 transition-all" style={{ width: `${stats.pctDone}%` }} title={`Completed ${stats.pctDone}%`} />
                    <div className="bg-blue-400 transition-all" style={{ width: `${stats.pctInProgress}%` }} title={`In Progress ${stats.pctInProgress}%`} />
                    <div className="bg-red-400 transition-all" style={{ width: `${stats.pctOverdue}%` }} title={`Overdue ${stats.pctOverdue}%`} />
                </div>
                <div className="flex items-center gap-2">
                    <span className="flex items-center gap-0.5 text-[9px] text-emerald-600"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />Done</span>
                    <span className="flex items-center gap-0.5 text-[9px] text-blue-600"><span className="w-1.5 h-1.5 rounded-full bg-blue-400 inline-block" />In Progress</span>
                    <span className="flex items-center gap-0.5 text-[9px] text-red-500"><span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" />Overdue</span>
                    <span className="flex items-center gap-0.5 text-[9px] text-slate-400"><span className="w-1.5 h-1.5 rounded-full bg-slate-300 inline-block" />Not Started</span>
                </div>
            </div>

            {stats.staffWorkload?.length > 0 && (
                <>
                    <div className="w-px bg-slate-200 mx-1 self-stretch shrink-0" />
                    <div className="flex-1 min-w-0 flex flex-col justify-center gap-0.5 mx-1 px-2 py-1 bg-white rounded border border-slate-200 shadow-sm">
                        <span className="text-[9px] font-semibold text-slate-400 uppercase">{staffLoadTitle} ({stats.key.toUpperCase()})</span>
                        <div className="flex items-center gap-1 flex-wrap">
                            {stats.staffWorkload.slice(0, 4).map(({ id, name, count }) => (
                                <button
                                    key={id}
                                    type="button"
                                    onClick={() => onToggleStaffFilter?.(id)}
                                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 border rounded-full text-[9px] font-medium transition-all ${String(activeStaffFilter) === String(id) ? 'bg-indigo-50 border-indigo-300 text-indigo-700 ring-1 ring-indigo-200' : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'}`}
                                    title={String(activeStaffFilter) === String(id) ? `Clear staff filter for ${name}` : `Filter by ${name}`}
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-blue-400 inline-block shrink-0" />
                                    <span className="truncate max-w-[70px]">{name}</span>
                                    <span className="text-blue-600 font-bold shrink-0">{count}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default WorkstreamKpiStrip;
